import { useEffect, useState } from 'react';
import { Button, Container, ProgressBar } from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import { useQuestionnaireTemplates } from '../../hooks/useQuestionnaireTemplates';
import { useAssessmentAnswers } from '../../hooks/useAssessmentAnswers';
import SectionViewer from '../questionnaire/SectionViewer';
import type {
  AnswerValue,
  QuestionnaireTemplate,
  Section,
} from '../../types/types';
import { validateSection } from '../../util/validateSection';
import { Link } from 'react-router-dom';
import { getLocalizedString } from '../../util/getLocalizedString';
import { tracking } from '../../util/tracking';

export default function AssessmentViewer({
  assessmentId,
}: {
  assessmentId: string;
}) {
  const [sections, setSections] = useState<Section[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [template, setTemplate] = useState<QuestionnaireTemplate>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState(false);
  const { getTemplateByAssessmentId } = useQuestionnaireTemplates();
  const {
    answers,
    setAnswers,
    saveAnswer,
    saveErrors,
    completeAssessment,
    loading: answersLoading,
    savingAnswer,
  } = useAssessmentAnswers(assessmentId);

  useEffect(() => {
    const fetchData = async () => {
      const template = await getTemplateByAssessmentId(assessmentId);
      if (!template) return;

      setTemplate(template);
      const { data: sectionsData } = await supabase
        .from('question_sections')
        .select('*')
        .eq('template_id', template.id)
        .eq('deleted', false)
        .order('ordinal', { ascending: true });

      const { data: questionsData } = await supabase
        .from('questions')
        .select('*')
        .eq('template_id', template.id)
        .eq('deleted', false)
        .order('ordinal', { ascending: true });

      if (sectionsData && questionsData) {
        const sectionsWithQuestions = sectionsData.map((s) => ({
          ...s,
          questions: questionsData.filter((q) => q.section_id === s.id),
        }));
        setSections(sectionsWithQuestions);
      }
    };
    fetchData();
  }, [assessmentId, getTemplateByAssessmentId]);

  const handleAnswerChange = (questionId: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    saveAnswer(questionId, value, questionId);

    // clear error as soon as the user fixes it
    if (errors[questionId]) {
      setErrors((prev) => {
        const newErr = { ...prev };
        delete newErr[questionId];
        return newErr;
      });
    }
  };

  const handleNext = () => {
    const { valid, errors } = validateSection(sections[currentIndex], answers);
    if (!valid) {
      setErrors(errors);
      //window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors({});
    setCurrentIndex((i) => i + 1);
  };

  const handlePrev = () => {
    setCurrentIndex((i) => i - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    const { valid, errors } = validateSection(sections[currentIndex], answers);
    if (!valid) {
      setErrors(errors);
      return;
    }
    const result = await completeAssessment();

    if (result.success) {
      // handle success
      setFinished(true);
      tracking.log('METRIC', 'assessment_submitted', { assessmentId });
    } else {
      // handle error
      tracking.log('ERROR', 'assessment_submission_error', {
        assessmentId,
        error: result.error?.message,
      });
      setError(true);
    }
  };

  if (!sections.length || answersLoading) return <p>Loading...</p>;

  const section = sections[currentIndex];
  const totalSections = sections.length;
  const progress = Math.round(((currentIndex + 1) / totalSections) * 100);

  return (
    <Container fluid style={{ maxWidth: '650px' }}>
      <div className="sticky-questionnaire-header bg-white pb-3">
        <h3>{template?.title}</h3>
        {!finished &&
          !error &&
          (template?.assessmentStatus === 'completed' ? (
            <span className="text-success">
              You are viewing a completed assessment{' '}
              <Link to="/">Back to Dashboard</Link>
            </span>
          ) : (
            <ProgressBar
              now={progress}
              label={`${currentIndex + 1} / ${totalSections}`}
            />
          ))}
      </div>

      {finished || error ? (
        <div className="border rounded-3 p-4 text-center bg-light">
          {finished && (
            <>
              <h4 className="text-success mb-3">✅ Assessment Complete!</h4>
              <p className="mb-0">
                Thank you for your time and effort. Your responses have been
                recorded.
              </p>
              {template?.organisationId ? (
                <Link to={`/assessments?tab=selfAssessments`}>
                  Return to Assessments
                </Link>
              ) : (
                <Link to="/">Return to Dashboard</Link>
              )}
            </>
          )}
          {error && (
            <>
              <h4 className="text-danger mb-3">❌ Submission Error</h4>
              <p className="mb-0">
                There was an error submitting your assessment. Please try again
                later.
              </p>
              <p>The support team has been notified of the issue.</p>
              <br />
            </>
          )}
        </div>
      ) : (
        <div className="p-3">
          <h5>{getLocalizedString(section.title) ?? 'Section'}</h5>
          {getLocalizedString(section.description) && (
            <div
              dangerouslySetInnerHTML={{
                __html: getLocalizedString(section.description) as string,
              }}
            ></div>
            // <p>{getLocalizedString(section.description)}</p>
          )}

          <SectionViewer
            section={section}
            answers={answers}
            onAnswerChange={handleAnswerChange}
            errors={{ ...saveErrors, ...errors }}
            savingAnswer={savingAnswer}
            completed={template?.assessmentStatus === 'completed'}
          />

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="secondary"
              onClick={handlePrev}
              disabled={currentIndex === 0 || savingAnswer}
            >
              Previous
            </Button>

            {Object.keys(errors).length > 0 && (
              <p className="text-danger">This section contains errors</p>
            )}
            {currentIndex < sections.length - 1 ? (
              <Button onClick={handleNext} disabled={savingAnswer}>
                Next
              </Button>
            ) : (
              <Button
                variant="success"
                onClick={handleSubmit}
                disabled={
                  savingAnswer || template?.assessmentStatus === 'completed'
                }
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      )}
    </Container>
  );
}
